import { MODALS, PAYMENT_STATUS_RESPONSE, SERVICE_STATUS_VALUES } from "../../../config/params";
import {
  PaymentMethodSection,
  PaymentPlanSection,
  PaymentProcessingOverlay,
  PurchaseButton,
  PurchaseSummarySection,
  ScratchPayPollingScreen,
  ScratchPayUnavailableBanner,
  TermsCheckbox,
} from "./components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getFilteredPaymentList, getPaymentSummary, getPrices } from "./helpers";
import {
  handleMonthlyPurchase,
  handleMonthlyRenewalPurchase,
  handleNormalPurchase,
  handleRenewalPurchase,
} from "./utils/purchaseHandlers";
import { useAllServiceStatusStore, useCombinedAlertsDataStore } from "../../../hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useScratchPayPurchaseFlow,
  useWellnessEligibility,
  useWellnessPurchase,
  useWellnessRenewal,
} from "./hooks";

import { Box } from "@mui/material";
import { MESSAGES } from "./constants";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { WellnessPlanErrorView } from "..";
import type { WellnessPlanLocalErrorKind } from "../WellnessPlanErrorPage/wellnessPlanError.helpers";
import WellnessPlanPetPlanCard from "../WellnessPlanPetPlanCard/WellnessPlanPetPlanCard";
import { billCycle } from "./types";
import { getSpeciesName } from "../../../components/PageTitle/WellnessPlanTitle/WellnessPlanTitle";
import { logPrequalEvent } from "../PrequalificationContainer/prequalAnalytics";
import { toastMessageActions } from "../../../state/request";
import { useCardDetailStore } from "../../../services/card-detail/card-detail.service";
import { useCardList } from "../../../services/card-list/card-list.service";
import { useDispatch } from "react-redux";
import { useFetchWellnessPlanByPetId } from "../../../services/wellness-plan/wellness-plan.service";
import { useForm } from "react-hook-form";
import { useModal } from "../../../context/ModalContext";
import { usePageTitleContext } from "../../../context/PageTitleContext";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useServiceStatus } from "../../../services/service-status/service-status.service";
import { useWellnessPlanErrorCta } from "../WellnessPlanErrorPage/useWellnessPlanErrorCta";
import useWellnessPurchaseData from "../../../hooks/redux-hooks/wellness-purchase-store/wellness-purchase-store";
import { userActivityLogger } from "../../../utils/mixpanel";

const WellnessPlanPaymentContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { wellnessPurchaseData } = useWellnessPurchaseData();
  const [billingCycle, setBillingCycle] = useState<billCycle>(
    wellnessPurchaseData?.preferredBillingCycle || "perMonth",
  );
  const [isScratchPayDenied, setIsScratchPayDenied] = useState(false);
  const [isProcessingOverlayDismissed, setIsProcessingOverlayDismissed] =
    useState(false);

  const { control, watch } = useForm({
    defaultValues: { isAgreed: false },
  });
  const isAgreed = watch("isAgreed");

  const { cardList, loading } = useCardList();
  const { petList } = usePetStore();
  const { openModal } = useModal();
  const { setBillCycle, setShowBackButtonOverride } = usePageTitleContext();
  const { endsSoonWPData } = useCombinedAlertsDataStore();
  const { setWellnessServiceStatus } = useAllServiceStatusStore();
  const { getServiceStatus, data } = useServiceStatus();
  const {
    getWellnessPlanByPetId,
    data: petWellnessPlanData,
    loading: petWellnessPlanLoading,
    error: petWellnessPlanError } = useFetchWellnessPlanByPetId();
  const { cardSelected, setCardSelected } = useCardDetailStore();
  const { eligible, eligibilityLoading, eligibilityError, refetchEligibility } = useWellnessEligibility();

  const petId = searchParams.get("id");

  const prequalStatus = wellnessPurchaseData?.prequalStatus;
  const isPrequalIneligible = prequalStatus === "INELIGIBLE";
  const currentPet = useMemo(
    () => petList?.filter((pet) => pet.id === petId) || [],
    [petList, petId],
  );
  const prices = getPrices(wellnessPurchaseData);
  const paymentSummary = getPaymentSummary(billingCycle, wellnessPurchaseData?.planName, prices);

  const isPetListLoaded = petList && petList.length > 0;
  const isInvalidPet = !petId || (isPetListLoaded && currentPet.length === 0);
  const isMissingPurchaseData = isPetListLoaded && !wellnessPurchaseData;

  useEffect(() => {
    getServiceStatus(SERVICE_STATUS_VALUES.WELLNESS_PLAN_SERVICE);

    // Log analytics if we have valid data
    if (petId && currentPet?.[0]) {
      userActivityLogger(MIXPANEL_EVENTS.VISIT_PAYMENT_CONFIRMATION_PAGE, {
        pet_id: petId,
        pet_name: currentPet?.[0]?.name,
        wellness_plan_code: wellnessPurchaseData?.planCode,
      });
    }
  }, []);

  useEffect(() => {
    if (
      data &&
      data.isActive &&
      cardList &&
      cardList.cards.length > 0 &&
      !cardSelected
    ) {
      const defaultSelected = cardList?.cards.filter((card) => card.isDefault);

      defaultSelected.length ?
        setCardSelected(defaultSelected[0]) :
        setCardSelected(cardList.cards[0]);
    }
  }, [cardList, data]);

  useEffect(() => {
    if (data) {
      if (petId && data.isActive) {
        setCardSelected(null);
        setBillCycle("perMonth");
      }
      if (data.isActive === false) {
        setWellnessServiceStatus(false);
      }
    }
  }, [petId, data]);

  useEffect(() => {
    if (
      !eligibilityLoading &&
      (eligible === false || isScratchPayDenied || isPrequalIneligible) &&
      billingCycle === "perMonth"
    ) {
      setBillingCycle("perYear");
      setBillCycle("perYear");
    }
  }, [eligible, eligibilityLoading, isScratchPayDenied, isPrequalIneligible]);

  useEffect(() => {
    if (petId) {
      getWellnessPlanByPetId(petId);
    }
  }, [petId]);

  const handleModals = (response: any) => {
    if (!currentPet) return;
    const params = {
      petName: currentPet[0].name,
      petId: petId,
      species: getSpeciesName(currentPet[0].species),
      wellnessPlanCode: wellnessPurchaseData?.planCode,
    };

    if (response.paymentStatus === PAYMENT_STATUS_RESPONSE.PENDING) {
      userActivityLogger(MIXPANEL_EVENTS.PAYMENT_PENDING_MODAL_SHOWN, {
        pet_name: currentPet[0].name,
        pet_id: currentPet[0].id,
        species: getSpeciesName(currentPet[0].species),
        wellnessPlanCode: wellnessPurchaseData?.planCode,
      });
      openModal(MODALS.WELLNESS_PLAN_PAYMENT_ERROR_MODAL, "", params);
    } else if (
      response.paymentStatus === PAYMENT_STATUS_RESPONSE.FAILED &&
      response.failedInvoiceLink
    ) {
      const source = response.failedInvoiceLink;

      userActivityLogger(
        MIXPANEL_EVENTS.PAYMENT_ISSUE_PAY_INVOICE_MODAL_SHOWN,
        {
          pet_name: currentPet[0].name,
          pet_id: currentPet[0].id,
          species: getSpeciesName(currentPet[0].species),
          wellnessPlanCode: wellnessPurchaseData?.planCode,
        },
      );
      openModal(MODALS.WELLNESS_PLAN_PAYMENT_ERROR_MODAL, source, params);
    } else if (response.paymentStatus === PAYMENT_STATUS_RESPONSE.SUCCESS) {
      navigate(`${PAGES_PATH.PET_CARE}?id=${petId}&category=TotalCare_Plan`);
    }
  };

  const { createSubscription, createSubscriptionLoading, error, resetCreateSubscription } = useWellnessPurchase({
    petId,
    currentPet,
    wellnessPurchaseData,
    billingCycle,
    prices,
    handleModals,
  });

  const { saveRenewalPreference, renewalPreferenceLoading, renewalPreferenceError } =
    useWellnessRenewal({
      petId,
      currentPet,
      wellnessPurchaseData,
      petWellnessPlanData,
      billingCycle,
      prices,
    });

  const handleScratchPayDenied = useCallback(() => {
    setIsScratchPayDenied(true);
    setBillingCycle("perYear");
    setBillCycle("perYear");
  }, [setBillCycle]);

  const {
    initiatePurchase,
    scratchPayPurchaseLoading,
    scratchPayPurchaseError,
    isPolling,
    cancelPolling: cancelScratchPayPolling,
    resetScratchPayPurchase,
  } = useScratchPayPurchaseFlow({ onPollingEnd: refetchEligibility, onDenied: handleScratchPayDenied });

  useEffect(() => {
    if (isPolling) {
      setShowBackButtonOverride(false);
      return;
    }

    setShowBackButtonOverride(null);
  }, [isPolling, setShowBackButtonOverride]);

  useEffect(() => {
    return () => {
      setShowBackButtonOverride(null);
      if (isPolling) {
        cancelScratchPayPolling();
      }
    };
  }, [setShowBackButtonOverride, cancelScratchPayPolling, isPolling]);

  const handleCardClick = (cycle: billCycle) => {
    if (prequalStatus) {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_BILLING_OPTION_SELECTED, {
        billing_frequency: cycle === "perMonth" ? "monthly" : "annual",
        result: prequalStatus === "ELIGIBLE" ? "approved" : "declined",
      });
    }
    setBillingCycle(cycle);
    setBillCycle(cycle);
  };

  const clearAllPurchaseErrors = useCallback(() => {
    resetCreateSubscription?.();
    resetScratchPayPurchase?.();
  }, [resetCreateSubscription, resetScratchPayPurchase]);

  const resolveErrorCta = useWellnessPlanErrorCta({
    petId,
    onTryAgain: clearAllPurchaseErrors,
  });

  const handlePurchase = () => {
    if (!petId) return;

    if (prequalStatus) {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_PURCHASE_FUNNEL_ENTERED, {
        funnel: billingCycle === "perMonth" ? "scratch_monthly" : "square_annual",
      });
    }

    const renewalAlert = endsSoonWPData.find(
      (alertPet) => alertPet.petId === petId,
    );

    if (billingCycle === "perMonth") {
      if (renewalAlert) {
        const scratchpayPlanId = wellnessPurchaseData?.scratchpayPlanId;

        if (!scratchpayPlanId) {
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "error",
              message: MESSAGES.PAYMENT_INPUT_DATA_ERROR,
            }),
          );
          return;
        }

        handleMonthlyRenewalPurchase({
          petId,
          currentPet,
          wellnessPurchaseData,
          prices,
          billingCycle,
          renewalAlert,
          saveRenewalPreference,
          dispatch,
          scratchpayPlanId,
        });
      } else {
        handleMonthlyPurchase({
          petId,
          currentPet,
          wellnessPurchaseData,
          prices,
          billingCycle,
          initiatePurchase,
          dispatch,
        });
      }
      return;
    }

    if (!cardSelected) return;

    setIsProcessingOverlayDismissed(false);

    if (renewalAlert) {
      handleRenewalPurchase({
        petId,
        currentPet,
        billingCycle,
        wellnessPurchaseData,
        prices,
        cardSelected,
        renewalAlert,
        saveRenewalPreference,
        dispatch,
      });
    } else {
      handleNormalPurchase({
        petId,
        currentPet,
        billingCycle,
        wellnessPurchaseData,
        prices,
        cardSelected,
        createSubscription,
      });
    }
  };

  const handleAddPayment = () => {
    openModal(MODALS.ADD_PAYMENT_METHOD);
  };

  const handleInfoClick = () => {
    openModal(MODALS.SCRATCHPAY_INFO_MODAL);
  };

  const getPaymentList = (cardList: any) => {
    return getFilteredPaymentList(cardList, cardSelected?.id || null);
  };

  const isServiceUnavailable = data?.isActive === false;

  const localErrorKind: WellnessPlanLocalErrorKind | null = (() => {
    if (isInvalidPet) return "PET_NOT_FOUND";
    if (isMissingPurchaseData) return "PLAN_NOT_FOUND";
    if (isServiceUnavailable) return "SERVICE_UNAVAILABLE";
    if (petWellnessPlanError) return "GENERIC";
    return null;
  })();

  if (localErrorKind) {
    return (
      <WellnessPlanErrorView resolveCta={resolveErrorCta} localKind={localErrorKind} />
    );
  }

  if (error || renewalPreferenceError || scratchPayPurchaseError) {
    const activeError = error || scratchPayPurchaseError || renewalPreferenceError;
    return (
      <WellnessPlanErrorView resolveCta={resolveErrorCta} apolloError={activeError} />
    );
  }

  if (isPolling) {
    const handleLeavePollingScreen = () => {
      cancelScratchPayPolling();
      navigate(`${PAGES_PATH.PET_CARE}?id=${petId ?? ""}`);
    };

    return <ScratchPayPollingScreen onLeave={handleLeavePollingScreen} />;
  }

  const isLoading =
    createSubscriptionLoading ||
    renewalPreferenceLoading ||
    petWellnessPlanLoading ||
    scratchPayPurchaseLoading ||
    eligibilityLoading ||
    isPolling;

  // Only show the processing overlay
  // for a brand-new annual subscription purchase.
  // Preference/renewal purchases (monthly or annual) should never show it.
  const isAnnualPurchaseProcessing =
    billingCycle === "perYear" && createSubscriptionLoading;

  const showProcessingOverlay =
    isAnnualPurchaseProcessing && !isProcessingOverlayDismissed;

  const isDisabled =
    isLoading ||
    !isAgreed ||
    (billingCycle === "perYear" && (!prices[billingCycle].amount || !cardSelected)) ||
    (billingCycle === "perMonth" &&
      (!!eligibilityError || eligible === false || isScratchPayDenied || isPrequalIneligible)) ||
    isPolling;

  const showScratchPayWarningBanner =
    !eligibilityLoading && (eligible === false || isScratchPayDenied || isPrequalIneligible);

  return (
    <Box className="w-full flex flex-col sm:flex-row sm:justify-between gap-6">
      <PaymentProcessingOverlay
        open={showProcessingOverlay}
        onClose={() => setIsProcessingOverlayDismissed(true)}
      />
      <Box
        className="flex flex-col gap-2 relative -top-12 md:-top-16 xl:-top-32
        sm:w-[90%] md:w-[60%] xl:w[40%] 2xl:w-[660px]"
      >
        {showScratchPayWarningBanner && (
          <ScratchPayUnavailableBanner
            message={
              eligible === false ?
                MESSAGES.SCRATCHPAY_NOT_ELIGIBLE :
                MESSAGES.SCRATCHPAY_DENIED
            }
          />
        )}

        <Box className="flex flex-col bg-white border border-taupe-50 rounded-lg pb-4 w-full overflow-hidden">
          <WellnessPlanPetPlanCard
            loading={loading}
            fromPaymentContainer={true}
            disableChangePlan={isLoading}
          />

          <PaymentPlanSection
            annualPrice={prices.perYear.amount || 0}
            monthlyPrice={prices.perMonth.amount || 0}
            billingCycle={billingCycle}
            loading={loading}
            eligibilityLoading={eligibilityLoading}
            eligibilityError={eligibilityError}
            eligible={eligible}
            isScratchPayDenied={isScratchPayDenied}
            prequalStatus={prequalStatus}
            handleCardClick={handleCardClick}
            onInfoClick={handleInfoClick}
          />

          <PaymentMethodSection
            billingCycle={billingCycle}
            cardList={cardList || null}
            loading={loading}
            getPaymentList={getPaymentList}
            handleAddPayment={handleAddPayment}
          />
        </Box>
      </Box>

      <Box className="relative -top-12 md:-top-16 xl:-top-[8.5rem] sm:w-[90%] md:w-[45%]">
        <PurchaseSummarySection paymentSummary={paymentSummary} loading={loading} billingCycle={billingCycle} />
        <TermsCheckbox control={control} />
        <PurchaseButton
          billingCycle={billingCycle}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onClick={handlePurchase}
        />
      </Box>
    </Box>
  );
};

export default WellnessPlanPaymentContainer;
